<template>
  <div class="property-sold-manual" v-show="selected">
    <div class="form-group">
      <p>Sale details</p>

      <date-input
        :label="form.soldDate.label"
        @validated-fields="validate"
        @input="onInput"
      />

      <date-input
        class = "subsection"
        :label="form.settlementDate.label"
        :isMandatory="true"
        @validated-fields="validate"
        @input="onInput"
      />

      <value-input
        class = "subsection"
        :valueInputIds="['yes','no']"
        :isMandatory="true"
        :label="form.saleUnconditional.label"
        @validated-fields="validate"
        @input="onSaleUnconditionalInput"
      />

      <date-input
        class = "subsection"
        v-if="saleIsUnconditional"
        :label="form.unconditionalDate.label"
        :isMandatory="saleIsUnconditional"
        @validated-fields="validate"
        @input="onInput"
      />

      <number-input
        class="subsection"
        :label="form.soldPrice.label"
        :placeholder="'Enter the sold amount'"
        :isMandatory="true"
        @validated-fields="validate"
        @input="onInput"
      />

      <value-input
        class="subsection"
        :valueInputIds="['fixed_d','incommission','free','notprovided']"
        :label="form.marketingCost.label"
        @validated-fields="validate"
        @input="onInput"
      />

      <div class="subsection">
        <label for="comments">Comments</label>
        <textarea id="comments" rows="5" v-model="comment" />
      </div>

      <div class="subsection">
        <oa-button
          :text="'Submit & mark as sold'"
          :btnClass="{ color: 'green' }"
          :disabled="disableValidateButton"
          @click="clickedValidate"
          v-gaEvent="gaEvent('Leads Management - Click update lead submit - Sold')"
        />
      </div>
    </div>
  </div>
</template>

<script>

import { mapState } from 'vuex';
import { LeadAgentLeadStageType } from '@/Mapper/LeadAgentLeadStageType';
import { LeadStageType, LeadStageTypeId } from '@/Mapper/LeadStageType';
import OaButton from '@/components/Forms/OaButton.vue';
import DateInput from '@/components/Forms/DateInput.vue';
import ValueInput from '@/components/Forms/ValueInput.vue';
import NumberInput from '@/components/Forms/NumberInput.vue';
import StageUpdate from '@/mixins/StageUpdate';
import LeadAgentLeadStageChangeSold from '@/mixins/LeadAgentLeadStageChangeSold';
import StageChangeSold from '@/mixins/StageChangeSold';
import FormHandler from '@/mixins/FormHandler';

export default {
  name: 'PropertySoldManual',
  mixins: [
    StageUpdate,
    LeadAgentLeadStageChangeSold,
    StageChangeSold,
    FormHandler,
  ],
  components: {
    OaButton,
    DateInput,
    NumberInput,
    ValueInput,
  },
  computed: {
    ...mapState({
      agent: state => state.agentStore.agent,
      user: state => state.userStore.user,
      lam: state => state.leadAgentMappingStore.lam,
      lead: state => state.leadAgentMappingStore.lead,
    }),
  },
  props: {
    selected: {
      default: false,
    },
  },
  data() {
    return {
      disableSubmit: false,
      form: {
        marketingCost: {
          label: 'Marketing costs',
          values: {},
        },
        unconditionalDate: {
          label: 'Unconditional date',
          values: {},
          isHidden: !this.saleIsUnconditional,
        },
        settlementDate: {
          label: 'Settlement date',
          values: {},
        },
        soldDate: {
          label: 'Sold date',
          values: {},
        },
        saleUnconditional: {
          label: 'Is the sale unconditional?',
          values: {},
        },
        soldPrice: {
          label: 'Sale price',
          values: {},
        },
      },
      comment: '',
    };
  },
  methods: {
    isNumber(evt) {
      const event = (evt) || window.event;
      const charCode = (event.which) ? event.which : event.keyCode;
      if ((charCode > 31 && (charCode < 48 || charCode > 57)) && charCode !== 46) {
        event.preventDefault();
        return false;
      }
      return true;
    },
    getMarketingCostId() {
      switch (this.form.marketingCost.values.select) {
        case 'fixed_d':
          return 1;
        case 'range_d':
          return 2;
        case 'incommission':
          return 3;
        case 'free':
          return 4;
        case 'notprovided':
          return 5;
        default:
          return '';
      }
    },
    createCaseForSaleUpdate() {
      const caseData = {
        leadkey: this.lead.key,
        agentkey: this.$route.params.ak,
        // agentkey: this.agent.key,
        leadstatus: LeadStageTypeId[LeadStageType.Sold],
        comment: this.comment,
      };
      return this.$apiServices.oa.lead.createCaseForLeadUpdate(caseData);
    },
    saveDetails() {
      this.disableSubmit = true;
      Promise.all([
        this.saveMarketingDetails(),
        this.saveSoldDetails(),
        this.soldUpdates(),
      ]).then(() => {
        this.disableSubmit = false;
      });
    },
    saveMarketingDetails() {
      const data = {
        leadagentmappingid: this.lam.id,
        oauserkey: !!this.user && !!this.user.key ? this.user.key : this.agent.userkey,
        // oauserkey: this.agent.userkey,
        leadagentleadstagetypeid: LeadAgentLeadStageType.Listed,
        marketingcosttypeid: this.getMarketingCostId(),
        marketingcostamount: (this.form.marketingCost.values.select !== ''
          ? this.form.marketingCost.values.value1
          : ''
        ),
        sourcetypeid: this.isLamOwner ? 1 : 5,
      };
      return this.$apiServices.oa.leadAgent.saveMarketingCostDetails(data);
    },
    saveSoldDetails() {
      const data = {
        leadagentmappingid: this.lam.id,
        oauserkey: !!this.user && !!this.user.key ? this.user.key : this.agent.userkey,
        // oauserkey: this.agent.userkey,
        solddate: this.form.soldDate.values.value !== ''
          ? this.$helpers.formatDateApi(this.form.soldDate.values.value)
          : '',
        settlementdate: this.form.settlementDate.values.value !== ''
          ? this.$helpers.formatDateApi(this.form.settlementDate.values.value)
          : '',
        saleunconditionaldate: this.form.unconditionalDate.values.value !== ''
          ? this.$helpers.formatDateApi(this.form.unconditionalDate.values.value)
          : '',
        saleunconditionalid: this.saleIsUnconditional
          ? 1
          : 0,
        soldprice: this.form.soldPrice.values.value1,
        soldmarketingcost: '',
        solddetail: this.comment,
        sourcetypeid: this.isLamOwner ? 1 : 5,
      };
      return this.$apiServices.oa.leadAgent.saveSoldDetails(data);
    },
  },
};

</script>

<style lang="scss" scoped>

.property-sold-manual .btn {
  margin-bottom: 15px;
}

.property-sold-manual .subsection {
  @apply ut-pt-6;
}

.property-sold-manual .subsection input[type='checkbox'] {
  @apply ut-w-auto;
}

.property-sold-manual p {
  @apply ut-font-normal ut-text-lg ut-pb-3;
  line-height: 28px;
}

.checkbox-text {
  @apply ml-4;
}

.form-group .error {
  label {
    color: #CA3B30;
  }
}

</style>
